import {
  CheckCheck,
  CircleAlert,
  CircleCheckBig,
  Eye,
  ListChecks,
  LoaderCircle,
  RotateCcw,
  Undo2,
} from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'

import { Button } from '../../../ui/Button/Button'
import {
  REQUEST_LABELS,
  completionsSince,
  requestSource,
  requestSummary,
  statusTime,
  summarizeRequests,
} from '../action-request-presentation'
import { conversationAttention } from '../conversation-attention'
import { useCloseOnEscape } from './use-close-on-escape'
import type {
  InteractionActionRequest,
  InteractionOperationalState,
} from '../interaction-operational-contracts'
import type { ActionRequestsView } from '../use-action-requests'
import styles from '../ConversationsPage.module.css'

type Picker = 'complete' | 'reopen' | null

function RequestPicker({
  mode,
  actions,
  candidates,
  onClose,
}: {
  mode: 'complete' | 'reopen'
  actions: ActionRequestsView
  candidates: InteractionActionRequest[]
  onClose: () => void
}) {
  const titleId = useId()
  const dialog = useRef<HTMLDivElement>(null)
  // Focus lands on the question, not an answer: a repeated key never confirms.
  useEffect(() => {
    dialog.current?.focus()
  }, [])
  useCloseOnEscape(onClose)
  const [chosen, setChosen] = useState<Set<string>>(
    () => new Set(candidates.map((item) => item.actionRequestId))
  )
  const selected = candidates.filter((item) => chosen.has(item.actionRequestId))
  const loading =
    candidates.length === 0 &&
    (actions.requests.status === 'loading' ||
      actions.requests.status === 'idle')
  const failed = candidates.length === 0 && actions.requests.status === 'error'
  const title =
    mode === 'complete'
      ? candidates.length === 1
        ? 'Mark this request completed?'
        : `Mark ${candidates.length} requests completed?`
      : candidates.length === 1
        ? 'Reopen this completed request?'
        : `Reopen ${candidates.length} completed requests?`

  return (
    <div
      ref={dialog}
      className={styles.statusConfirm}
      role="dialog"
      aria-labelledby={titleId}
      tabIndex={-1}
    >
      {candidates.length > 0 ? (
        <>
          <strong id={titleId}>{title}</strong>
          <ul>
            {candidates.map((item) => (
              <li key={item.actionRequestId}>
                {candidates.length > 1 ? (
                  <label className={styles.pickerRow}>
                    <input
                      type="checkbox"
                      checked={chosen.has(item.actionRequestId)}
                      onChange={(event) => {
                        const checked = event.target.checked
                        setChosen((current) => {
                          const next = new Set(current)
                          if (checked) next.add(item.actionRequestId)
                          else next.delete(item.actionRequestId)
                          return next
                        })
                      }}
                    />
                    <span>{REQUEST_LABELS[item.requestType]}</span>
                    <small>{requestSource(item)}</small>
                  </label>
                ) : (
                  <>
                    <span>{REQUEST_LABELS[item.requestType]}</span>
                    <small>{requestSource(item)}</small>
                  </>
                )}
              </li>
            ))}
          </ul>
          <p>
            {mode === 'complete'
              ? 'It’s recorded as handled by staff and cleared from Needs attention. You can reopen it later.'
              : 'It returns to Needs action for every staff member. No message is sent to the patient.'}
          </p>
        </>
      ) : loading ? (
        <strong id={titleId} className={styles.statusLoading}>
          <LoaderCircle
            className={styles.statusSpinner}
            size={15}
            aria-hidden="true"
          />
          Loading requests…
        </strong>
      ) : failed ? (
        <>
          <strong id={titleId}>Requests couldn’t be loaded</strong>
          <p className={styles.statusConfirmError} role="alert">
            This conversation’s requests couldn’t be loaded. Try again.
          </p>
        </>
      ) : (
        <>
          <strong id={titleId}>Already up to date</strong>
          <p>
            Another staff member already updated these requests. The status
            refreshes automatically.
          </p>
        </>
      )}
      <div className={styles.statusConfirmActions}>
        <Button variant="ghost" type="button" onClick={onClose}>
          Cancel
        </Button>
        {failed ? (
          <Button
            variant="secondary"
            type="button"
            onClick={() => void actions.reload()}
          >
            Try again
          </Button>
        ) : null}
        {candidates.length > 0 ? (
          <Button
            type="button"
            disabled={selected.length === 0}
            onClick={() => {
              onClose()
              void (mode === 'complete'
                ? actions.complete(selected)
                : actions.reopen(selected))
            }}
          >
            {mode === 'complete' ? 'Mark completed' : 'Reopen'}
          </Button>
        ) : null}
      </div>
    </div>
  )
}

// Exactly one status per conversation (conversationAttention). Pending states
// are colored and carry the one action that settles them; settled states carry
// a check mark and only a quiet correction.
export function ConversationStatusStrip({
  operationalState,
  activityAtMs,
  actions,
  reviewBusy,
  onToggleReviewed,
}: {
  operationalState: InteractionOperationalState
  activityAtMs: number
  actions?: ActionRequestsView
  reviewBusy: boolean
  onToggleReviewed?: () => void
}) {
  const [picker, setPicker] = useState<Picker>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const attention = conversationAttention(operationalState)
  // What is shown follows what can be read; what is offered follows what the
  // server enabled for this clinic.
  const supported = Boolean(actions?.supported)
  const items = actions?.readable ? actions.requests.items : []
  const open = items.filter((item) => item.status === 'OPEN')
  const settled =
    attention === 'NONE' ? completionsSince(items, activityAtMs) : []
  const settledByAction = settled.length > 0
  const reopenable = settled.filter((item) => item.reopenable)
  const busy = actions?.busy ?? null
  const candidates =
    picker === 'complete' ? open : picker === 'reopen' ? reopenable : []

  // A picker belongs to the status that opened it.
  useEffect(() => {
    setPicker(null)
  }, [attention, settledByAction])

  // After a command settles, focus lands on the new status's own action.
  const wasBusy = useRef(false)
  useEffect(() => {
    if (wasBusy.current && !busy) trigger.current?.focus()
    wasBusy.current = Boolean(busy)
  }, [busy])

  const latest = settled.reduce(
    (value, item) => Math.max(value, item.resolution?.resolvedAtMs ?? 0),
    0
  )
  const reopenedAt = open.reduce(
    (value, item) => Math.max(value, item.reopenedAtMs ?? 0),
    0
  )
  const pickerFor = (mode: 'complete' | 'reopen') =>
    picker === mode && actions ? (
      <RequestPicker
        // A new set of requests is a new question: all of them start chosen.
        key={candidates.map((item) => item.actionRequestId).join(',')}
        mode={mode}
        actions={actions}
        candidates={candidates}
        onClose={() => {
          setPicker(null)
          trigger.current?.focus()
        }}
      />
    ) : null

  return (
    <div
      className={styles.statusStrip}
      role="group"
      aria-label="Conversation status"
    >
      {busy ? (
        <div
          className={`${styles.statusTrack} ${styles.trackPending}`}
          role="status"
        >
          <span className={styles.statusIcon} aria-hidden="true">
            <LoaderCircle
              className={styles.statusSpinner}
              size={16}
              strokeWidth={2}
            />
          </span>
          <span className={styles.statusCopy}>
            <strong>
              {busy === 'complete'
                ? 'Completing action…'
                : 'Reopening request…'}
            </strong>
            <span>Updating it for every staff member</span>
          </span>
        </div>
      ) : attention === 'NEEDS_ACTION' ? (
        <div className={`${styles.statusTrack} ${styles.trackAction}`}>
          <span className={styles.statusIcon} aria-hidden="true">
            <CircleAlert size={16} strokeWidth={2.25} />
          </span>
          <span className={styles.statusCopy}>
            <strong>Needs action</strong>
            <span>
              {requestSummary(
                operationalState.unresolvedActionRequestTypes,
                operationalState.unresolvedActionRequestCount
              )}
              {reopenedAt ? ` · Reopened ${statusTime(reopenedAt)}` : ''}
            </span>
          </span>
          {supported && actions ? (
            <Button
              ref={trigger}
              className={styles.statusButton}
              variant="secondary"
              type="button"
              disabled={reviewBusy}
              aria-expanded={picker === 'complete'}
              onClick={() => {
                const opening = picker !== 'complete'
                setPicker(opening ? 'complete' : null)
                if (opening && actions.requests.status === 'error') {
                  void actions.reload()
                }
              }}
            >
              <ListChecks size={15} aria-hidden="true" /> Mark action completed
            </Button>
          ) : null}
          {pickerFor('complete')}
        </div>
      ) : attention === 'NEEDS_REVIEW' ? (
        <div className={`${styles.statusTrack} ${styles.trackReview}`}>
          <span className={styles.statusIcon} aria-hidden="true">
            <Eye size={16} strokeWidth={2} />
          </span>
          <span className={styles.statusCopy}>
            <strong>Needs review</strong>
            <span>
              {operationalState.reviewedThroughActivityAtMs
                ? 'New activity since the last review'
                : 'Not reviewed yet'}
            </span>
          </span>
          <Button
            className={styles.statusButton}
            variant="secondary"
            type="button"
            disabled={reviewBusy || !onToggleReviewed}
            onClick={onToggleReviewed}
          >
            <CheckCheck size={15} aria-hidden="true" /> Mark reviewed
          </Button>
        </div>
      ) : settledByAction && actions ? (
        <div className={`${styles.statusTrack} ${styles.trackCompleted}`}>
          <span className={styles.statusIcon} aria-hidden="true">
            <CircleCheckBig size={16} strokeWidth={2.25} />
          </span>
          <span className={styles.statusCopy}>
            <strong>Action completed</strong>
            <span>
              {summarizeRequests(settled)} · {statusTime(latest)}
            </span>
          </span>
          {!supported ? null : actions.undoable ? (
            <Button
              ref={trigger}
              className={styles.statusUndo}
              variant="ghost"
              type="button"
              onClick={() => void actions.undo()}
              aria-label="Undo action completion"
            >
              <Undo2 size={14} aria-hidden="true" /> Undo
            </Button>
          ) : reopenable.length > 0 ? (
            <Button
              ref={trigger}
              className={styles.statusUndo}
              variant="ghost"
              type="button"
              aria-expanded={picker === 'reopen'}
              onClick={() =>
                setPicker((current) => (current === 'reopen' ? null : 'reopen'))
              }
            >
              <RotateCcw size={14} aria-hidden="true" /> Reopen
            </Button>
          ) : null}
          {supported ? pickerFor('reopen') : null}
        </div>
      ) : (
        <div className={`${styles.statusTrack} ${styles.trackReviewed}`}>
          <span className={styles.statusIcon} aria-hidden="true">
            <CheckCheck size={16} strokeWidth={2} />
          </span>
          <span className={styles.statusCopy}>
            <strong>Reviewed</strong>
            {operationalState.reviewedAtMs ? (
              <span>{statusTime(operationalState.reviewedAtMs)}</span>
            ) : null}
          </span>
          <Button
            className={styles.statusUndo}
            variant="ghost"
            type="button"
            disabled={reviewBusy || !onToggleReviewed}
            onClick={onToggleReviewed}
            aria-label="Undo review"
            title="Undo review"
          >
            <Undo2 size={14} aria-hidden="true" /> Undo
          </Button>
        </div>
      )}
      {actions?.notice ? (
        actions.notice.tone === 'failure' ? (
          <p className={styles.statusError} role="alert">
            {actions.notice.message}
          </p>
        ) : (
          <p className={styles.statusNotice} role="status">
            {actions.notice.message}
          </p>
        )
      ) : null}
    </div>
  )
}
